import { useEffect, useCallback } from 'react'

/**
 * Props for the useOrderNavigation hook
 */
interface UseOrderNavigationProps {
  /** Whether the drawer is open */
  open: boolean
  /** Current index in the orders array */
  currentIndex: number
  /** Total number of orders */
  totalOrders: number
  /** Callback when navigating to a different order */
  onNavigate?: (index: number) => void
}

/**
 * Return type for the useOrderNavigation hook
 */
interface UseOrderNavigationReturn {
  /** Navigate to the previous order */
  handlePrevious: () => void
  /** Navigate to the next order */
  handleNext: () => void
}

/**
 * Hook to handle order navigation with keyboard shortcuts
 */
export function useOrderNavigation({
  open,
  currentIndex,
  totalOrders,
  onNavigate,
}: UseOrderNavigationProps): UseOrderNavigationReturn {
  // Navigate to the previous order
  const handlePrevious = useCallback(() => {
    if (currentIndex > 0 && onNavigate) {
      onNavigate(currentIndex - 1)
    }
  }, [currentIndex, onNavigate])

  // Navigate to the next order
  const handleNext = useCallback(() => {
    if (currentIndex < totalOrders - 1 && onNavigate) {
      onNavigate(currentIndex + 1)
    }
  }, [currentIndex, totalOrders, onNavigate])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return

      // Ignore if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }

      // Handle arrow keys for order navigation
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        e.stopPropagation()
        handlePrevious()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        e.stopPropagation()
        handleNext()
      }
    }

    window.addEventListener('keydown', handleKeyDown, true) // Use capture phase
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [open, handlePrevious, handleNext])

  return {
    handlePrevious,
    handleNext,
  }
}
